import { Button } from '@mui/material';
import { Chain } from '@ankr.com/chains-list';
import { Copy } from '@ankr.com/ui';
import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { JWT } from 'domains/jwtToken/types';
import { setSelectedTokenIndex } from 'domains/jwtToken/store/jwtTokenManagerSlice';
import { useAccount } from 'modules/account/hooks/useAccount';
import { useDialog } from 'modules/common/hooks/useDialog';
import { useTranslation } from 'modules/i18n/hooks/useTranslation';

import { CopyEndpointModalContent } from './CopyEndpointModalContent';
import { endpointModalTranslation } from './translation';
import { useCopyEndpointModalStyles } from './useCopyEndpointModalStyles';

interface ICopyEndpointModalProps {
  chain: Chain;
  userEndpointToken: string;
  jwtTokens: JWT[];
  buttonProps?: React.ComponentProps<typeof Button>;
  isProtocolSwitcherHidden?: boolean;
  buttonClassName?: string;
  isIconButton?: boolean;
  hasProjectSelector?: boolean;
}

export const CopyEndpointModalWrapper = ({
  buttonClassName,
  buttonProps,
  chain,
  hasProjectSelector,
  isIconButton,
  isProtocolSwitcherHidden = false,
  jwtTokens,
  userEndpointToken,
}: ICopyEndpointModalProps) => {
  const { isOpened, onClose: onClickCloseButton, onOpen } = useDialog();

  const dispatch = useDispatch();

  const { accountAddress = '' } = useAccount();

  const handleSelectTokenIndex = useCallback(
    (newIndex: number) => {
      dispatch(
        setSelectedTokenIndex({
          tokenIndex: newIndex,
          address: accountAddress,
        }),
      );
    },
    [accountAddress, dispatch],
  );

  const currentProjectIndex = useMemo(
    () =>
      jwtTokens.find(token => token.userEndpointToken === userEndpointToken)
        ?.index || 0,
    [jwtTokens, userEndpointToken],
  );

  const onClickEndpointButton = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event?.stopPropagation();
      event?.preventDefault();
      handleSelectTokenIndex(currentProjectIndex);
      onOpen();
    },
    [currentProjectIndex, handleSelectTokenIndex, onOpen],
  );

  const { classes, cx } = useCopyEndpointModalStyles();

  const { keys, t } = useTranslation(endpointModalTranslation);

  return (
    <>
      <Button
        size="small"
        className={cx(classes.copyEndpointButton, buttonClassName)}
        {...buttonProps}
        onClick={buttonProps?.onClick || onClickEndpointButton}
      >
        {isIconButton ? <Copy /> : t(keys.copyEndpoint)}
      </Button>
      {isOpened && (
        <CopyEndpointModalContent
          chain={chain}
          hasProjectSelector={hasProjectSelector}
          isProtocolSwitcherHidden={isProtocolSwitcherHidden}
          jwtTokens={jwtTokens}
          onClickCloseButton={onClickCloseButton}
          isOpened={isOpened}
          currentProjectIndex={currentProjectIndex}
        />
      )}
    </>
  );
};
